import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
// Fonturile temelor magazinului, pentru mostrele din editorul de aspect (doar @font-face; se descarcă la nevoie).
import "@fontsource-variable/instrument-sans";
import "@fontsource-variable/bodoni-moda";
import "@fontsource-variable/jost";
import "@fontsource-variable/alegreya";
import "@fontsource/alegreya-sans/400.css";
import "@fontsource-variable/archivo/wdth.css";
import "@fontsource-variable/inter";
import "@fontsource-variable/playfair-display";
import "@fontsource-variable/fraunces";
import "@fontsource-variable/nunito";
import "@fontsource-variable/space-grotesk";
import "@fontsource-variable/lora";
import { installErrorReporting } from "./lib/errors";
import { ApiError } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import { Spinner } from "./components/ui";
import Login from "./pages/Login";
import Impersonate from "./pages/Impersonate";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Legal from "./pages/Legal";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import ProductEdit from "./pages/ProductEdit";
import Categories from "./pages/Categories";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import Customers from "./pages/Customers";
import Discounts from "./pages/Discounts";
import Shipping from "./pages/Shipping";
import Settings from "./pages/Settings";
import Pages, { PageEdit } from "./pages/Pages";
import SeoPage from "./pages/Seo";
import Aspect from "./pages/Aspect";
import Support from "./pages/Support";
import SupportTicket from "./pages/SupportTicket";

installErrorReporting();

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (n, e) => !(e instanceof ApiError && e.problem.status < 500) && n < 2,
      refetchOnWindowFocus: true,
    },
  },
});

function Protected() {
  const { authenticated, booting, loading } = useAuth();
  if (booting) return <Spinner />;
  if (!authenticated) return <Navigate to="/login" replace />;
  if (loading) return <Spinner />;
  return <Layout />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/impersonate" element={<Impersonate />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/legal/:doc" element={<Legal />} />
            <Route element={<Protected />}>
              <Route index element={<Dashboard />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="products" element={<Products />} />
              <Route path="products/new" element={<ProductEdit />} />
              <Route path="products/:id" element={<ProductEdit />} />
              <Route path="categories" element={<Categories />} />
              <Route path="customers" element={<Customers />} />
              <Route path="discounts" element={<Discounts />} />
              <Route path="shipping" element={<Shipping />} />
              <Route path="pages" element={<Pages />} />
              <Route path="pages/new" element={<PageEdit />} />
              <Route path="pages/:id" element={<PageEdit />} />
              <Route path="aspect" element={<Aspect />} />
              <Route path="seo" element={<SeoPage />} />
              <Route path="settings" element={<Settings />} />
              <Route path="support" element={<Support />} />
              <Route path="support/:id" element={<SupportTicket />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
